import punycode from 'punycode/';
import twitterText from 'twitter-text';

import { CodeMirrorMark, MarksHelper } from '../code-mirror-wrapper/marks-helper';
import { CodemirrorContext } from '../codemirror-context';

export class CodemirrorLinksMentionsAndHashtags {
  private linksMarks: CodeMirrorMark[] = [];
  private mentionMarks: CodeMirrorMark[] = [];
  private hashtagMarks: CodeMirrorMark[] = [];

  private previousEditorContent: string;

  constructor(private codedmirrorContext: CodemirrorContext) {}

  /*
   * Note that the codemirror instance must have been created before calling this method
   */
  init() {
    // according to the doc, cursorActivity will be fired when the cursor or selection moves, or any change is made to the editor content.
    // This seems to include gaining the focus (but not losing the focus, surprinsingly)
    this.previousEditorContent = this.getCodeMirror().getValue();
    this.codedmirrorContext.cursorActivity$.subscribe(() => this.updateMarksAndLinksIfEditorChanged());
  }

  private updateMarksAndLinksIfEditorChanged() {
    const currentEditorContent = this.getCodeMirror().getValue();
    if (currentEditorContent !== this.previousEditorContent) {
      // We only update the mentions when the editor content has changed, so as not to mess with the spellchecker
      // (CodeMirror seems to create new DOM nodes for all marks even when removing and adding mentions and hastag marks)
      this.previousEditorContent = currentEditorContent;
      this.detectLinks();
      if (this.codedmirrorContext.hasMentionDetector) {
        this.detectMentionsAndHastags();
      }
    }
  }

  /**
   * Called when the editor changes, to update the links, both as marks ans in the coresponding SingleMessage
   */
  private detectLinks() {
    this.linksMarks.forEach(mark => mark.clear());
    this.linksMarks = [];
    this.codedmirrorContext.message.links = [];
    let currentLineOffsetCP = 0;
    this.getCodeMirror()
      .getDoc()
      .eachLine(line => {
        this.detectLinksFromLine(line, currentLineOffsetCP);
        const lineLength: number = punycode.ucs2.decode(line.text).length;
        currentLineOffsetCP += lineLength + 1; // +1 for the newline character
      });
  }

  private detectLinksFromLine(line, currentLineOffsetCP: number) {
    const extractedLinksFromLine = twitterText.extractUrlsWithIndices(line.text);

    // first pass: create CodeMirror marks, with lineCh units (expressed in javascript string offsets)
    for (const extractedLink of extractedLinksFromLine) {
      const fromTo = this.convertTwitterTextResult(extractedLink, line.lineNo());
      this.linksMarks.push(MarksHelper.createEditor3LinkMark(this.getCodeMirror(), fromTo.from, fromTo.to));
    }

    // second pass: create Editor2Links, with offsets expressed in unicode characters
    twitterText.modifyIndicesFromUTF16ToUnicode(line.text, extractedLinksFromLine);
    for (const extractedLink of extractedLinksFromLine) {
      this.codedmirrorContext.message.links.push({
        start: currentLineOffsetCP + (extractedLink.indices[0] as number),
        end: currentLineOffsetCP + (extractedLink.indices[1] as number),
        url: extractedLink.url,
      });
    }
  }

  /**
   * Called when the editor changes, to update the mentions and hashtag marks
   */
  private detectMentionsAndHastags() {
    this.mentionMarks.forEach(mark => mark.clear());
    this.mentionMarks = [];
    this.hashtagMarks.forEach(mark => mark.clear());
    this.hashtagMarks = [];
    const mentions = [];
    const hashtags = [];
    this.getCodeMirror()
      .getDoc()
      .eachLine(line => {
        mentions.push(...this.getMentionsFromLine(line));
        hashtags.push(...this.getHashtagsFromLine(line));
      });
    for (const mention of mentions) {
      this.mentionMarks.push(MarksHelper.createTwitterMentionMark(this.getCodeMirror(), mention.from, mention.to));
    }
    for (const hashtag of hashtags) {
      this.hashtagMarks.push(MarksHelper.createTwitterHashtagMark(this.getCodeMirror(), hashtag.from, hashtag.to));
    }
  }

  private getMentionsFromLine(line) {
    return twitterText
      .extractMentionsWithIndices(line.text)
      .map(mention => this.convertTwitterTextResult(mention, line.lineNo()));
  }

  private getHashtagsFromLine(line) {
    return twitterText
      .extractHashtagsWithIndices(line.text)
      .map(mention => this.convertTwitterTextResult(mention, line.lineNo()));
  }

  private convertTwitterTextResult(extracted, lineNo) {
    return {
      from: this.newLineCh(lineNo, extracted.indices[0]),
      to: this.newLineCh(lineNo, extracted.indices[1]),
    };
  }

  private newLineCh(line, ch) {
    return { line, ch };
  }

  private getCodeMirror() {
    return this.codedmirrorContext.codeMirror;
  }
}
